import React, { useState } from "react";
import { Drawer, Input, Form, Button, Checkbox, Col, Row } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import PasswordStrength from "../../../../components/UI/PasswordStrength";

interface Props {
  show: boolean;
  hide: any;
}

const ResetPasswordFormDrawer: React.FC<Props> = ({ show, hide }) => {
  const [form] = Form.useForm();
  const [password, setPassword] = useState("");

  return (
    <>
      <Drawer
        title="Reset password"
        placement="right"
        closable={true}
        onClose={hide}
        visible={show}
        width={500}
        className="menu-drawer"
      >
        <Row
          gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
          justify="center"
          className="pt-4"
        >
          <Col md={22}>
            <Form layout="vertical" name="recruiter-general-info" form={form}>
              <Form.Item
                name="emailAddress"
                label="Email Address"
                rules={[
                  {
                    type: "email",
                    message: "The input is not valid E-mail!",
                  },
                  {
                    required: true,
                    message: "Please input your E-mail!",
                  },
                ]}
              >
                <Input
                  prefix={<i className="far fa-envelope font-14" />}
                  placeholder="Enter Email address"
                  readOnly
                  defaultValue="bella@mailinator.com"
                />
              </Form.Item>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  {
                    required: true,
                    message: "Please input your password!",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      // Check if password meets all requirements
                      const hasMinLength = value.length >= 8;
                      const hasNumber = /\d/.test(value);
                      const hasSpecialChar =
                        /[!@#$%^&*()+\-=\[\]{}|;':",.<>?/\\]/.test(value);
                      const hasUppercase = /[A-Z]/.test(value);
                      const hasLowercase = /[a-z]/.test(value);

                      const allRequirementsMet =
                        hasMinLength &&
                        hasNumber &&
                        hasSpecialChar &&
                        hasUppercase &&
                        hasLowercase;

                      if (allRequirementsMet) {
                        return Promise.resolve();
                      }

                      // Show first unmet requirement (in order from screenshot)
                      if (!hasMinLength)
                        return Promise.reject(
                          "Password must be at least 8 characters"
                        );
                      if (!hasNumber)
                        return Promise.reject("Password must contain number");
                      if (!hasSpecialChar)
                        return Promise.reject(
                          "Password must contain special character"
                        );
                      if (!hasUppercase)
                        return Promise.reject(
                          "Password must contain uppercase letter"
                        );
                      if (!hasLowercase)
                        return Promise.reject(
                          "Password must contain lowercase letter"
                        );

                      return Promise.resolve();
                    },
                  },
                ]}
                hasFeedback
              >
                <Input.Password
                  prefix={<LockOutlined className="site-form-item-icon" />}
                  placeholder="Enter Password"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Form.Item>
              <PasswordStrength password={password} />

              <Form.Item
                name="confirm"
                label="Confirm Password"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: "Please confirm your password!",
                  },
                  {
                    min: 8,
                    message: "Password must be at least 8 characters long",
                  },
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        "The two passwords that you entered do not match!"
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="site-form-item-icon" />}
                  placeholder="Enter confirm password"
                />
              </Form.Item>
              <Form.Item name="notify" valuePropName="checked">
                <Checkbox>Send reset password email to recruiter</Checkbox>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="ant-btn-teal float-right"
                  shape="round"
                  size="middle"
                >
                  Reset password
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Drawer>
    </>
  );
};

export default ResetPasswordFormDrawer;
